<script>
import { forEach, has } from 'lodash-es';
import MixedGridFiltersItemList from './MixedGridFiltersItemList';
import MixedGridFiltersItemRange from './MixedGridFiltersItemRange';
import MixedGridFiltersSelected from './MixedGridFiltersSelected';
import MixedGridFilters from '@phoenix/mixins/components/mixedGrid/MixedGridFilters';
import { getFilterItemFromIdAndValue } from '@phoenix/helpers/mixed-grid-helper';
import { showPanel, closePanel } from '@phoenix/helpers/panel-helper';
import PanelLegacy from '@maison/components/common/PanelLegacy.vue';
import { PhxIcon } from '@phx-styleguides/rdu';

export default {
  components: {
    MixedGridFiltersSelected,
    MixedGridFiltersItemList,
    MixedGridFiltersItemRange,
    PanelLegacy,
    PhxIcon,
  },

  mixins: [MixedGridFilters],

  emits: ['onFiltersChanged'],

  computed: {
    panelId() {
      return `${this.uniqueId}-filters-panel`;
    },
  },

  methods: {
    getFilterItemFromIdAndValue,

    showPanel,

    closePanel,

    initFilters() {
      forEach(this.$refs.filterPopover, (filter) => {
        new Toolkit.Collapse(filter, {
          closeOnClickOutside: true,
          replaceCollapse: true,
        });
      });

      forEach(this.$refs.filterCollapsible, (filter) => {
        new Toolkit.Collapse(filter, {
          closeOnClickOutside: false,
        });
      });
    },

    getActivatedFiltersById(id) {
      if (!has(this.activeFilters, id)) {
        return [];
      }

      const filter = this.getFilterById(id);

      if (filter?.component === 'range') {
        return [filter.label];
      }

      return this.activeFilters[id].map((item) => getFilterItemFromIdAndValue(this.filters, id, item).label);
    },

    getAccordionItemClasses(id) {
      return [
        'accordion-item',
        {
          'has-subtitles': this.getActivatedFiltersById(id).length > 0,
        },
      ];
    },
  },
};
</script>

<template>
  <!-- DESKTOP VERSION -->
  <div class="container hidden lg:block relative z-10" data-cy="mixed-grid-filters">
    <section
      aria-labelledby="filter-label"
      class="flex border-b border-white-50 flex-wrap flex-row"
      data-cy="mixed-grid-desktop-filters"
    >
      <ul class="collapses-container flex-grow">
        <li v-for="filter in filters" :key="`filter-${filter.id}-desktop`" class="relative inline-block">
          <button
            type="button"
            class="button button--menu button--light has-toggle"
            data-toggle="collapse"
            :data-target="`#${uniqueId}-collapsible-filter-${filter.id}`"
            :data-cy="filter.id"
            aria-expanded="false"
          >
            {{ filter.label }}
          </button>

          <div
            :id="`${uniqueId}-collapsible-filter-${filter.id}`"
            ref="filterPopover"
            data-group="filters"
            class="collapse collapse--popover"
          >
            <mixed-grid-filters-item-list
              v-if="filter.component === 'list'"
              :filter="filter"
              :active-filters="activeFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-range
              v-if="filter.component === 'range'"
              :filter="filter"
              :symbol="getSymbolFromFilter(filter)"
              :active-filters="activeFilters"
              @on-range-change="onRangeChange"
            />
          </div>
        </li>
      </ul>
    </section>
  </div>
  <!-- END DESKTOP VERSION -->
  <!-- MOBILE VERSION -->
  <div class="container lg:hidden flex flex-wrap sm:flex-no-wrap justify-between">
    <div class="p-2 w-full sm:w-auto">
      <button
        type="button"
        class="button button--md button--dark w-full justify-start sgphx"
        @click="showPanel(panelId)"
      >
        <phx-icon icon="filters" class="me-2" />
        {{ $t('Product.filters') }}
      </button>
    </div>
  </div>

  <panel-legacy :id="panelId" ref="filterPanel" :title="$t('Product.filters')" header-bordered footer-bordered>
    <template #content>
      <ul class="accordion menu mobile-collapse-container" data-cy="mixed-grid-mobile-filters">
        <li v-for="filter in filters" :key="`filter-${filter.id}-mobile`" :class="getAccordionItemClasses(filter.id)">
          <button
            class="accordion__title menu__toggle menu__entry"
            data-toggle="collapse"
            :data-target="`#filter-mobile-${filter.id}`"
            aria-expanded="false"
          >
            {{ filter.label }}
            <span
              v-if="getActivatedFiltersById(filter.id).length > 0 && filter.component !== 'range'"
              class="menu__subtitle"
            >
              {{ getActivatedFiltersById(filter.id).join(', ') }}
            </span>
          </button>

          <div :id="`filter-mobile-${filter.id}`" ref="filterCollapsible" class="accordtion__content collapse">
            <mixed-grid-filters-item-list
              v-if="filter.component === 'list'"
              :is-mobile="true"
              :filter="filter"
              :active-filters="activeFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-range
              v-if="filter.component === 'range'"
              :filter="filter"
              :symbol="getSymbolFromFilter(filter)"
              :active-filters="activeFilters"
              @on-range-change="onRangeChange"
            />
          </div>
        </li>
      </ul>
    </template>

    <template #footer>
      <div class="sgphx">
        <div class="sgphx-actions sgphx-actions--full">
          <button
            type="button"
            class="sgphx-btn sgphx-btn--primary sgphx-btn--positive"
            :data-tracking="datalayerTrackingViewResults"
            @click="closePanel(panelId)"
          >
            <span class="sgphx-btn__text">
              <span>{{ $t('Search.applyFilters', { productsCount: productsCount }) }}</span>
            </span>
          </button>

          <button type="button" class="sgphx-btn sgphx-btn--primary button-reset" @click="() => removeAllFilters()">
            <span class="sgphx-btn__text">
              <span>{{ $t('Search.ResetAll') }}</span>
            </span>
          </button>
        </div>
      </div>
    </template>
  </panel-legacy>
  <!-- END MOBILE VERSION -->
  <mixed-grid-filters-selected
    :filters="filters"
    :active-filters="activeFilters"
    @remove-filter="removeFilter"
    @remove-all-filters="removeAllFilters"
  />
  <div class="h5 text-white text-center pt-8" data-cy="mixed-grid-products-count">
    {{ productCountLabel }}
  </div>
</template>
